import './progressReport.css';
import type {
  ProgressReportDto, ReportItemDto, ItemReportState,
} from '../learnerPlanApi';

// The printable progress report (BP 25/27). Pure presentation — every number
// it shows is computed server-side by LearnerPlanReportService so a student's
// copy and the instructor's copy cannot drift apart, and so nothing here has
// to decide what "complete" means.
//
// Two rules this component follows and should keep following:
//
//   1. IT NEVER FILLS A GAP. A null renders as "—" or as the words "no
//      information recorded"; it never renders as 0, as an empty bar, or as
//      a blank cell that reads like a zero. `report.caveats` is rendered in
//      full, always, and is not collapsible — the gaps in a real student's
//      record are part of the document, not a footnote to hide.
//   2. IT ADDS NO ANALYSIS OF ITS OWN. The summary sentences and the trend
//      decision both arrive from the server. If the server says a trend is
//      not available, this renders the server's reason rather than drawing
//      something anyway.
//
// LAYOUT (2026-09 redesign). The substance was right and the hierarchy was
// not: it read as one undifferentiated column of stacked text and five
// identical stat tiles, so the eye had nowhere to land. It is now ordered the
// way an instructor actually reads it —
//
//      WHO  →  WHERE THEY STAND  →  WHAT NEEDS ACTION  →  THE DETAIL
//
// with a type scale and rules carrying the structure instead of everything
// arriving at the same weight. The "needs action" band is new; it is not new
// INFORMATION — the counts come from `plan.counts` and the rows are the
// server's own per-item states filtered for display, exactly as the existing
// sort already filtered them for order. Nothing is computed here that the
// server did not compute.
//
// NO CHART WAS ADDED, deliberately. The one candidate was a stacked bar of the
// plan states, and it is the wrong picture: a bar makes "no information
// recorded" a slice the same kind as "complete", when the entire point of this
// document is that the two are different claims. A chart that flattens that
// distinction would undo the best thing about the report to decorate six
// numbers. The trend bars below are left exactly as they were — the server
// already refuses to draw them below four marked results.
//
// The PDF is the browser's Print to PDF over progressReport.css's @media
// print block. There is no PDF library in this bundle on purpose.

const STATE_ORDER: ItemReportState[] = [
  'needs_retry', 'in_progress', 'not_started', 'awaiting_result', 'no_information', 'complete',
];

/** The states that mean a student has something to do about an item. Used only
 * to pick which of the server's rows to LIST in the action band; the counts
 * beside them are the server's. */
const ACTIONABLE: ItemReportState[] = ['needs_retry', 'in_progress', 'not_started'];

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtScore(item: ReportItemDto): string {
  if (item.score == null) return '—';
  if (item.max_score == null) return String(item.score);
  return `${item.score} / ${item.max_score}`;
}

/** Sort so the things a student has to act on are at the top of the page they
 * actually read, and everything finished falls to the bottom. */
function sortItems(items: ReportItemDto[]): ReportItemDto[] {
  return [...items].sort((a, b) => {
    if (a.past_due !== b.past_due) return a.past_due ? -1 : 1;
    const sa = STATE_ORDER.indexOf(a.state);
    const sb = STATE_ORDER.indexOf(b.state);
    if (sa !== sb) return sa - sb;
    const da = a.due?.date ?? '9999-12-31';
    const db = b.due?.date ?? '9999-12-31';
    if (da !== db) return da.localeCompare(db);
    return a.title.localeCompare(b.title);
  });
}

/** A headline figure. `tone` shades the rule down its left edge only — never a
 * fill — so it survives a printer that drops backgrounds, which is the one
 * that prints this document in a college office. */
function Tile({
  label, value, note, tone, emphasis,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'plain' | 'attention' | 'unknown';
  emphasis?: boolean;
}) {
  const cls = [
    'lpr-tile',
    tone && tone !== 'plain' ? `lpr-tile-${tone}` : '',
    emphasis ? 'lpr-tile-lead' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <dt>{label}</dt>
      <dd>
        {value}
        {note ? <span className="lpr-tile-note">{note}</span> : null}
      </dd>
    </div>
  );
}

export interface IssuedStamp {
  issued_at: string;
  /** Shown as "Issued by <name>" when the surface knows it. Optional because a
   * student's own copy does not need to name the instructor to be honest about
   * the date, which is the part that matters. */
  issued_by_name?: string | null;
}

export default function ProgressReport({
  report, issued,
}: {
  report: ProgressReportDto;
  /** Present when this is an ISSUED SNAPSHOT rather than a live view. It
   * changes what the document claims about itself, so it is shown in the
   * masthead and again in the sign-off, not tucked into a caption. */
  issued?: IssuedStamp | null;
}) {
  const { student, course, term, plan, effort, trend } = report;
  const items = sortItems(plan.items);
  const attendance = effort.attendance;

  // What needs action, drawn from the server's per-item states. `overdue` is
  // "past due and not complete"; `pastDueUnknown` is the honest other half —
  // past due with NOTHING recorded either way, which is not the same accusation.
  //
  // Each filter below reproduces EXACTLY the membership rule the server used
  // for the matching count, and the count shown beside it is the server's own
  // — never `list.length`. So the band can never claim a number the document
  // does not also justify item by item. "Next due" is deliberately NOT a tile
  // here: the server already writes that sentence into `summary`, and a second
  // client-side version of the same rule is precisely the fork that lets an
  // instructor's copy and a student's copy disagree.
  const overdue = items.filter((i) => i.past_due && ACTIONABLE.includes(i.state));
  const retry = items.filter((i) => i.state === 'needs_retry');
  const pastDueUnknown = items.filter((i) => i.past_due && i.state === 'no_information');
  const hasActions = overdue.length > 0 || retry.length > 0 || pastDueUnknown.length > 0;

  return (
    <article className="lpr">
      <div className="lpr-page">
        {/* ── WHO ───────────────────────────────────────────────────────── */}
        <header className="lpr-masthead">
          <div className="lpr-masthead-main">
            <p className="lpr-doctype">Progress report</p>
            <h1 className="lpr-title">{student.name}</h1>
            <p className="lpr-subtitle">
              {`Student number: ${student.student_id || 'not recorded'}`}
              {student.email ? ` · ${student.email}` : ''}
            </p>
          </div>
          <div className="lpr-idblock">
            <div className="lpr-idblock-course">
              {course.code ? `${course.code} — ` : ''}{course.title}
            </div>
            {term.first_meeting && term.last_meeting ? (
              <div>{fmtDate(term.first_meeting)} to {fmtDate(term.last_meeting)}</div>
            ) : null}
            {issued ? (
              <div className="lpr-issued">
                <strong>Issued {fmtDate(issued.issued_at)}</strong>
                {issued.issued_by_name ? <span> by {issued.issued_by_name}</span> : null}
              </div>
            ) : (
              <div>Prepared {fmtDate(report.generated_at)}</div>
            )}
          </div>
        </header>

        {issued ? (
          <p className="lpr-issued-banner">
            This report was issued on {fmtDate(issued.issued_at)} and shows where things stood
            on that date. It does not change afterwards — if anything has been recorded or
            corrected since, it is not reflected here and your instructor would need to issue
            a new one.
          </p>
        ) : null}

        {/* ── WHERE THEY STAND ──────────────────────────────────────────── */}
        <section className="lpr-section lpr-section-lead">
          <h2 className="lpr-h-lead">Where you stand</h2>
          <ul className="lpr-summary">
            {report.summary.map((line) => <li key={line}>{line}</li>)}
          </ul>

          <dl className="lpr-tiles lpr-tiles-lead">
            <Tile
              emphasis
              label="Complete"
              value={`${plan.counts.complete} of ${plan.counts.total}`}
              note="items with a recorded result"
            />
            <Tile
              emphasis
              label="Points so far"
              value={plan.points.items_counted > 0
                ? `${plan.points.earned} / ${plan.points.out_of}`
                : '—'}
              note={plan.points.items_counted > 0
                ? `across ${plan.points.items_counted} of ${plan.points.items_total} items · not a course grade`
                : 'no marked items yet'}
            />
            <Tile
              emphasis
              label="Attendance"
              value={attendance.eligible > 0 ? `${attendance.percent}%` : '—'}
              note={attendance.eligible > 0
                ? `${attendance.present} of ${attendance.eligible} meetings where attendance was taken`
                : 'attendance has not been taken yet'}
              tone={attendance.eligible > 0 ? 'plain' : 'unknown'}
            />
          </dl>
        </section>

        {/* ── WHAT NEEDS ACTION ─────────────────────────────────────────── */}
        <section className="lpr-section lpr-section-action">
          <h2 className="lpr-h-lead">What needs attention</h2>

          {!hasActions ? (
            <p className="lpr-note lpr-note-calm">
              Nothing in the plan is past its due date or below the pass mark.
              {plan.counts.no_information > 0
                ? ` ${plan.counts.no_information} of ${plan.counts.total} items still have no result recorded at all — that is not a zero, and not a statement that nothing was submitted.`
                : ''}
            </p>
          ) : (
            <div className="lpr-actions">
              {overdue.length > 0 && (
                <div className="lpr-action lpr-action-urgent">
                  <p className="lpr-action-head">
                    <span className="lpr-action-count">{plan.counts.overdue}</span>
                    past the due date and not complete
                  </p>
                  <ul className="lpr-action-list">
                    {overdue.map((i) => (
                      <li key={i.key}>
                        <span className="lpr-action-title">{i.title}</span>
                        <span className="lpr-action-meta">
                          due {i.due?.date || i.due?.label || 'unknown'} · {i.state_label.toLowerCase()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {retry.length > 0 && (
                <div className="lpr-action lpr-action-urgent">
                  <p className="lpr-action-head">
                    <span className="lpr-action-count">{plan.counts.needs_retry}</span>
                    below the pass mark — can be retried
                  </p>
                  <ul className="lpr-action-list">
                    {retry.map((i) => (
                      <li key={i.key}>
                        <span className="lpr-action-title">{i.title}</span>
                        <span className="lpr-action-meta">{fmtScore(i)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {pastDueUnknown.length > 0 && (
                <div className="lpr-action lpr-action-unknown">
                  <p className="lpr-action-head">
                    <span className="lpr-action-count">{plan.counts.past_due_no_information}</span>
                    past due with no result recorded
                  </p>
                  <ul className="lpr-action-list">
                    {pastDueUnknown.map((i) => (
                      <li key={i.key}>
                        <span className="lpr-action-title">{i.title}</span>
                        <span className="lpr-action-meta">due {i.due?.date || i.due?.label || 'unknown'}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="lpr-note">
                    Whether these were submitted is not known to this platform — check DC Connect.
                    They are not marks of zero.
                  </p>
                </div>
              )}
            </div>
          )}

          <dl className="lpr-tiles lpr-tiles-quiet">
            <Tile
              label="Awaiting a result"
              value={String(plan.counts.awaiting_result)}
              note="submitted, not yet marked"
            />
            <Tile
              label="No information"
              value={String(plan.counts.no_information)}
              note="not the same as a zero"
              tone="unknown"
            />
            <Tile
              label="In progress"
              value={String(plan.counts.in_progress)}
              note="started, not yet finished"
            />
          </dl>
        </section>

        {/* ── THE DETAIL: the item table ────────────────────────────────── */}
        <section className="lpr-section lpr-section-plan">
          <h2>Every item in the plan</h2>
          <div className="lpr-scroll" style={{ overflowX: 'auto' }}>
            <table className="lpr-table">
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col">Due</th>
                  <th scope="col">State</th>
                  <th scope="col" className="lpr-num">Result</th>
                  <th scope="col">Recorded</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.key}>
                    <td>
                      <div>{item.title}</div>
                      <div className="lpr-item-key">
                        {item.item_type.replace(/_/g, ' ')}
                        {item.points != null ? ` · ${item.points} pts` : ''}
                      </div>
                    </td>
                    <td className={item.past_due && item.state !== 'complete' ? 'lpr-overdue' : undefined}>
                      {item.due ? item.due.date || item.due.label : '—'}
                      {item.past_due && item.state !== 'complete' ? ' (past due)' : ''}
                    </td>
                    <td>
                      <span className={`lpr-state lpr-state-${item.state}`}>{item.state_label}</span>
                    </td>
                    <td className="lpr-num">{fmtScore(item)}</td>
                    <td>
                      {item.observed_at
                        ? `${fmtDate(item.observed_at)}${item.source === 'instructor' ? ' (instructor)' : ''}`
                        : '—'}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={5}>No assessment plan has been set up for this course yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="lpr-note">
            "No information recorded" means this platform has not been told anything about the
            item — it is not a mark of zero and not a statement that nothing was submitted.
          </p>
        </section>

        {/* ── Effort ── */}
        <section className="lpr-section lpr-break-before">
          <h2>Effort and engagement</h2>
          <dl className="lpr-tiles">
            <Tile
              label="Attendance"
              value={attendance.eligible > 0 ? `${attendance.percent}%` : '—'}
              note={attendance.eligible > 0
                ? `${attendance.present} of ${attendance.eligible} meetings where attendance was taken`
                : 'attendance has not been taken yet'}
              tone={attendance.eligible > 0 ? 'plain' : 'unknown'}
            />
            <Tile
              label="Class activities"
              value={effort.participation.expected > 0
                ? `${effort.participation.responded} / ${effort.participation.expected}`
                : '—'}
              note={effort.participation.expected > 0 ? 'meetings you took part in' : 'none run yet'}
              tone={effort.participation.expected > 0 ? 'plain' : 'unknown'}
            />
            <Tile
              label="Lessons"
              value={effort.lessons.total > 0
                ? `${effort.lessons.completed} / ${effort.lessons.total}`
                : '—'}
              note={effort.lessons.xp ? `${effort.lessons.xp} XP earned` : undefined}
            />
            <Tile
              label="Check questions"
              value={effort.quiz_checks.attempts > 0
                ? `${effort.quiz_checks.accuracy_percent}%`
                : '—'}
              note={effort.quiz_checks.attempts > 0
                ? `${effort.quiz_checks.correct} correct of ${effort.quiz_checks.attempts} attempted`
                : 'none attempted yet'}
              tone={effort.quiz_checks.attempts > 0 ? 'plain' : 'unknown'}
            />
            <Tile
              label="Sign-ins"
              value={String(effort.engagement.logins)}
              note={effort.engagement.last_seen_at
                ? `last seen ${fmtDate(effort.engagement.last_seen_at)}`
                : 'never signed in'}
            />
          </dl>

          {attendance.sessions.length > 0 && (
            <>
              <h3>Meeting by meeting</h3>
              <div className="lpr-timeline">
                {attendance.sessions.map((s) => (
                  <div key={s.seq} className="lpr-timeline-cell" title={s.label}>
                    {s.cell}
                    <span>M{s.seq}</span>
                  </div>
                ))}
              </div>
              <p className="lpr-note">
                P = present (checked in during class) · M = marked present by the instructor ·
                R = remote exception · A = absent · — = attendance was not taken at that meeting.
                {attendance.voided > 0 ? ` ${attendance.voided} check-in(s) were voided and count as absent.` : ''}
              </p>
            </>
          )}
        </section>

        {/* ── Trend ── */}
        <section className="lpr-section">
          <h2>Results over the term</h2>
          {trend.available ? (
            <>
              <div className="lpr-trend">
                {trend.points.map((p) => (
                  <div
                    key={`${p.item_key}-${p.at}`}
                    className="lpr-trend-bar"
                    style={{ height: `${Math.max(2, Math.min(100, p.percent))}%` }}
                    title={`${p.item_title}: ${p.percent}%`}
                  />
                ))}
              </div>
              <div className="lpr-trend-labels">
                {trend.points.map((p) => (
                  <div key={`${p.item_key}-${p.at}-label`}>{p.percent}%</div>
                ))}
              </div>
              <p className="lpr-note">
                Each bar is one marked item, oldest on the left:{' '}
                {trend.points.map((p) => p.item_title).join(', ')}.
              </p>
            </>
          ) : (
            <p className="lpr-note">{trend.reason}</p>
          )}
        </section>

        {/* ── Caveats: never collapsible, never abbreviated ── */}
        <section className="lpr-section">
          <h2>What this report does and does not show</h2>
          <ul className="lpr-caveats">
            {report.caveats.map((c) => <li key={c}>{c}</li>)}
          </ul>
        </section>

        <footer className="lpr-signoff">
          <div>
            First-hand records kept by this platform: {report.sources.first_hand.join(', ')}.
            {report.sources.mirrored.length
              ? ` Mirrored from: ${report.sources.mirrored.join(', ')}.`
              : ' No marks have been mirrored from DC Connect.'}
          </div>
          <div>
            {issued
              ? `Issued ${fmtDate(issued.issued_at)} to ${student.name}`
              : `Prepared ${fmtDate(report.generated_at)} for ${student.name}`}
            {student.student_id ? ` (${student.student_id})` : ''} · {course.title}.
          </div>
          <div className="lpr-print-hint">
            {issued
              ? `This document was issued on ${fmtDate(issued.issued_at)} and shows where things stood on that date; it does not change afterwards.`
              : `This is a printed copy, accurate as of ${fmtDate(report.generated_at)} — the live report may have changed since.`}
            {' '}If you need page numbers, turn on "Headers and footers" in the browser's
            print dialog; a web page cannot draw them itself.
          </div>
        </footer>
      </div>
    </article>
  );
}
